<ion-content #content [fullscreen]="true" class="viva-page control-container">
  <main id="page-main" class="layout-page-container chat-page-container">
    <div class="chat-header-row">
      <div class="chat-header-left">
        <viva-back-button *ngIf="backAllowed()" (backClick)="goBack()"></viva-back-button>
      </div>

      <div class="chat-header-center">
        <h1 class="page-title">{{ 'media-chat-bot-page.title' | translate }}</h1>
      </div>

      <div class="ai-chat-icon">
        <viva-chat color="white" [interactive]="false" (onBotClicked)="aiChatClicked()"></viva-chat>
      </div>
    </div>

    <div class="message-container">
      <div
        class="message"
        *ngFor="let msg of chatMessages; let i = index"
        [ngClass]="{
          'user-message': msg.sender === 'user',
          'response-message': msg.sender === 'bot',
          'fade-in-message': i === chatMessages.length - 1
        }"
        #lastMessage
        [attr.data-index]="i"
      >
        <!-- bottom sentinel near the most recent user message -->
        <div *ngIf="i >= chatMessages.length - 2 && msg.sender !== 'bot'" #bottomSentinel></div>

        <!-- Bot response -->
        <div *ngIf="msg.sender === 'bot'" class="chat-response" #botMessage>
          <div [innerHTML]="msg.text"></div>
        </div>

        <!-- Web search results -->
        <div *ngIf="msg.webSearchResults?.length" class="web-results">
          <h1>{{ 'media-chat-bot-page.web-results-title' | translate }}:</h1>
          <div class="web-result" *ngFor="let result of msg.webSearchResults">
            <h2><strong>{{ result.title }}</strong></h2>
            <p>
              <strong>{{ 'media-chat-bot-page.web-summary' | translate }}:</strong>
              {{ result.fullContentSummarized }}
            </p>
            <a *ngIf="result.url" [href]="result.url" target="_blank">
              {{ 'media-chat-bot-page.read-more-web' | translate }}
            </a>
          </div>
        </div>

        <!-- Article -->
        <div *ngIf="msg.article" class="article-text">
          <h2>{{ msg.article.mediaItemName }}</h2>

          <div class="audio-player" *ngIf="msg.article.audioURL">
            <div class="audio-controls">
              <ion-icon name="mic-outline" class="audio-icon"></ion-icon>
              <button class="play-button" (click)="togglePlay(audioPlayer, msg)">
                <ion-icon [name]="msg.isPlaying ? 'pause' : 'play'" class="play-icon"></ion-icon>
              </button>
              <div class="progress-bar-container">
                <input type="range" min="0" [max]="msg.audioDuration" [(ngModel)]="msg.currentTime" (input)="seekAudio(msg, $event)"
                  class="progress-bar">
              </div>
              <span class="time">{{ formatTime(msg.currentTime) }} / {{ formatTime(msg.audioDuration) }}</span>
            </div>
            <audio #audioPlayer (timeupdate)="updateTime(msg)" (loadedmetadata)="setAudioDuration(msg, audioPlayer)"
              controlsList="nodownload">
              <source [src]="msg.article.audioURL" type="audio/mp3">
            </audio>
          </div>

          <div [innerHTML]="msg.article.htmlContent"></div>
        </div>

        <!-- User fallback -->
        <span *ngIf="msg.sender !== 'bot'">{{ msg.text }}</span>

        <!-- Feedback + Read more -->
        <div class="button-container" *ngIf="msg.sender === 'bot'">
          <div class="thumbs-container">
            <span style="margin: 5px;">{{ 'media-chat-bot-page.answer-relevant' | translate }}</span>

            <ion-button
              class="read-more-button"
              size="small"
              fill="solid"
              [disabled]="busyLoading"
              [attr.aria-pressed]="msg.feedback === 'up'"
              [ngClass]="{ 'selected-thumb': msg.feedback === 'up' }"
              (click)="thumbsUp(msg.aiResponseId!, true)">
              <ion-icon
                slot="icon-only"
                name="thumbs-up-outline"
                aria-hidden="true"></ion-icon>
            </ion-button>

            <ion-button
              class="read-more-button"
              size="small"
              fill="solid"
              [disabled]="busyLoading"
              [attr.aria-pressed]="msg.feedback === 'down'"
              [ngClass]="{ 'selected-thumb': msg.feedback === 'down' }"
              (click)="thumbsUp(msg.aiResponseId!, false)">
              <ion-icon
                slot="icon-only"
                name="thumbs-down-outline"
                aria-hidden="true"></ion-icon>
            </ion-button>
          </div>

          <ion-button
            fill="solid"
            class="read-more-button"
            [disabled]="busyLoading"
            *ngIf="!msg.article && msg.citation?.mediaArticleId"
            [color]="busyLoading ? 'medium' : 'dark'"
            (click)="viewCitations(msg)">
            {{ 'media-chat-bot-page.read-more' | translate }}
          </ion-button>
        </div>

        <!-- Thinking dots under the last user message -->
        <div *ngIf="i === chatMessages.length - 1 && msg.sender !== 'bot'">
          <div *ngIf="busyLoading" class="thinking-indicator">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </div>
    </div>
  </main>
</ion-content>

<!-- Fixed Footer (keyboard will push this thanks to .has-fixed-footer) -->
<ion-footer #inputContainer class="chat-footer" [translucent]="false">
  <div class="footer-stack layout-page-container">
    <div class="message-input-container">
      <div class="chat-input-box">
        <ion-textarea
          #chatTextarea
          [(ngModel)]="message"
          autoGrow="true"
          placeholder="{{ 'media-chat-bot-page.user-message' | translate }}"
          [disabled]="busyLoading"
          class="chat-textarea"
          (keydown.enter)="onEnter($event)">
        </ion-textarea>

        <div class="chat-actions" *ngIf="message && !busyLoading">
          <ion-button
            fill="outline"
            class="action-button web-toggle-button"
            [color]="enableWebSearch ? 'dark' : 'medium'"
            (click)="enableWebSearch = !enableWebSearch">
            <ion-icon class="white-icon" name="globe-outline" slot="start"></ion-icon>
            {{ enableWebSearch
              ? ('media-chat-bot-page.search-web-on' | translate)
              : ('media-chat-bot-page.search-web-off' | translate) }}
          </ion-button>

          <ion-button
            fill="outline"
            class="action-button send-action-button"
            [disabled]="busyLoading"
            [attr.aria-label]="'media-chat-bot-page.buttons.send' | translate"
            (click)="sendMessage()">
            <ion-icon class="send-button viva-icon--flip-rtl" name="send" slot="icon-only" aria-hidden="true"></ion-icon>
          </ion-button>
        </div>
      </div>
    </div>

    <div class="ai-notice">
      {{ 'media-chat-bot-page.ai-notice' | translate }}
    </div>
  </div>
</ion-footer>